import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cleanHtmlForPreview } from '../../utils/contentCleaner';
import { loadArticleHtml, prepareArticleQuill } from '../../utils/articlePaste';
import logger from '../../utils/logger';
import './Reading.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const READING_API = `${API_BASE_URL}/api/reading`;

const Reading = ({ onBack, selectedLanguage = 'English' }) => {
  const { t } = useTranslation('readingManagement');
  const [activePath, setActivePath] = useState(null);
  const [activeSchoolTab, setActiveSchoolTab] = useState('high-school');
  const [activeYearTab, setActiveYearTab] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editArticleId, setEditArticleId] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [yearArticleCounts, setYearArticleCounts] = useState({});
  const [debugEnabled, setDebugEnabled] = useState(false);
  const [debugEvents, setDebugEvents] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    contentType: '',
    schoolLevel: '',
    year: '',
    content: ''
  });
  const quillRef = useRef(null);
  const [useQuill, setUseQuill] = useState(false);
  // Additional path states
  const [examType, setExamType] = useState('IELTS');
  const [examSection, setExamSection] = useState('Academic');
  const [examDifficulty, setExamDifficulty] = useState('');
  const [espFaculty, setEspFaculty] = useState('Medicine');
  const [espSpecialty, setEspSpecialty] = useState('');
  const [espAcademicYear, setEspAcademicYear] = useState('');
  const [generalGenre, setGeneralGenre] = useState('');

  // ✅ ENGLISH KEYS (for API & database) - NEVER TRANSLATE THESE
  const SCHOOL_KEYS = {
    'high-school': 'High School',
    'middle-school': 'Middle School',
    'primary-school': 'Primary School'
  };

  const YEAR_KEYS = {
    'high-1': 'First Year',
    'high-2': 'Second Year',
    'high-3': 'Third Year',
    'middle-1': 'First Year',
    'middle-2': 'Second Year',
    'middle-3': 'Third Year',
    'middle-4': 'Fourth Year',
    'primary-1': 'First Year',
    'primary-2': 'Second Year',
    'primary-3': 'Third Year'
  };

  const examTypeOptions = ['IELTS', 'TOEFL', 'Cambridge'];
  const examSectionsMap = {
    IELTS: ['Academic', 'General Training', 'UKVI'],
    TOEFL: ['iBT', 'PBT', 'CBT'],
    Cambridge: ['FCE', 'CAE', 'CPE', 'PET', 'KET']
  };
  const difficultyOptions = ['Easy', 'Medium', 'Hard'];
  const facultyOptions = ['Medicine', 'Engineering', 'Business'];
  const generalGenres = ['story', 'news', 'blog', 'dialogue', 'poem', 'essay', 'novel'];

  // Reading Paths Configuration - UI labels (translated)
  const readingPaths = [
    {
      id: 'educational-levels',
      title: t('paths.educationalLevels.title'),
      icon: '📚',
      description: t('paths.educationalLevels.description'),
      color: '#007bff'
    },
    {
      id: 'international-exam',
      title: t('paths.internationalExam.title'),
      icon: '🌍',
      description: t('paths.internationalExam.description'),
      color: '#28a745'
    },
    {
      id: 'esp',
      title: t('paths.esp.title'),
      icon: '🎓',
      description: t('paths.esp.description'),
      color: '#ffc107'
    },
    {
      id: 'general',
      title: t('paths.general.title'),
      icon: '📖',
      description: t('paths.general.description'),
      color: '#6f42c1'
    }
  ];

  // School tabs - UI labels (translated)
  const schoolTabs = [
    { id: 'high-school', label: t('schoolLevels.highSchool'), icon: 'school' },
    { id: 'middle-school', label: t('schoolLevels.middleSchool'), icon: 'school' },
    { id: 'primary-school', label: t('schoolLevels.primarySchool'), icon: 'school' }
  ];

  // School levels - UI labels (translated) + English keys (for API)
  // NOTE: This is the base structure, language-agnostic.
  const baseSchoolLevels = {
    'high-school': {
      name: t('schoolLevels.highSchool'), // UI label (translated)
      key: SCHOOL_KEYS['high-school'], // English key for API
      years: [
        { id: 'high-1', name: t('yearLevels.firstYear'), key: YEAR_KEYS['high-1'] },
        { id: 'high-2', name: t('yearLevels.secondYear'), key: YEAR_KEYS['high-2'] },
        { id: 'high-3', name: t('yearLevels.thirdYear'), key: YEAR_KEYS['high-3'] }
      ]
    },
    'middle-school': {
      name: t('schoolLevels.middleSchool'), // UI label (translated)
      key: SCHOOL_KEYS['middle-school'], // English key for API
      years: [
        { id: 'middle-1', name: t('yearLevels.firstYear'), key: YEAR_KEYS['middle-1'] },
        { id: 'middle-2', name: t('yearLevels.secondYear'), key: YEAR_KEYS['middle-2'] },
        { id: 'middle-3', name: t('yearLevels.thirdYear'), key: YEAR_KEYS['middle-3'] },
        { id: 'middle-4', name: t('yearLevels.fourthYear'), key: YEAR_KEYS['middle-4'] }
      ]
    },
    'primary-school': {
      name: t('schoolLevels.primarySchool'), // UI label (translated)
      key: SCHOOL_KEYS['primary-school'], // English key for API
      years: [
        { id: 'primary-1', name: t('yearLevels.firstYear'), key: YEAR_KEYS['primary-1'] },
        { id: 'primary-2', name: t('yearLevels.secondYear'), key: YEAR_KEYS['primary-2'] },
        { id: 'primary-3', name: t('yearLevels.thirdYear'), key: YEAR_KEYS['primary-3'] }
      ]
    }
  };

  // Language-specific adjustments
  // In the Algerian system, some languages (e.g. German, Italian, Spanish)
  // are only studied in 2nd and 3rd year of High School.
  // We keep English API keys the same, but hide inappropriate levels/years in the admin UI.
  const lang = (selectedLanguage || '').toLowerCase();
  const isHighSchoolOnlyLanguage = ['german', 'italian', 'spanish'].includes(lang);

  const schoolLevels = isHighSchoolOnlyLanguage
    ? {
        ...baseSchoolLevels,
        'high-school': {
          ...baseSchoolLevels['high-school'],
          years: baseSchoolLevels['high-school'].years.filter((y) => y.id !== 'high-1')
        }
      }
    : baseSchoolLevels;

  // Quill setup
  useEffect(() => {
    logger.log('Quill setup effect - showForm:', showForm, 'Quill available:', !!window.Quill, 'quillRef:', !!quillRef.current, 'useQuill:', useQuill);
    
    if (showForm && window.Quill && quillRef.current && !useQuill) {
      logger.log('Initializing Quill editor...');
      setUseQuill(true);
      quillRef.current.innerHTML = '';
      
      try {
        const quill = new window.Quill(quillRef.current, {
          theme: 'snow',
          modules: {
            toolbar: [
              ['bold', 'italic', 'underline'],
              ['link', 'blockquote', 'code-block'],
              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
              [{ 'header': [1, 2, 3, false] }],
              ['clean']
            ]
          },
          placeholder: t('form.contentPlaceholder')
        });
        
        quill.on('text-change', () => {
          const content = quill.root.innerHTML;
          logger.log('Quill content changed:', content);
          setFormData(f => ({ ...f, content: content }));
        });
        prepareArticleQuill(quill);
        if (isEditing && formData.content) loadArticleHtml(quill, formData.content);
        
        logger.log('Quill editor initialized successfully');
      } catch (error) {
        logger.error('Error initializing Quill:', error);
        setUseQuill(false);
      }
    } else if (showForm && !window.Quill) {
      logger.log('Quill not available, using fallback textarea');
      setUseQuill(false);
    }
    // eslint-disable-next-line
  }, [showForm, isEditing, formData.content, t]);

  useEffect(() => {
    if (activePath === 'educational-levels') {
      setActiveSchoolTab('high-school');
      setActiveYearTab(null);
      setArticles([]);
      setError('');
      fetchYearArticleCounts();
    }
  }, [activePath]);

  useEffect(() => {
    if (activeYearTab && activePath === 'educational-levels') {
      fetchArticles();
    }
    // eslint-disable-next-line
  }, [activeYearTab, activePath]);

  // Fetch article counts for all years of the current school level
  async function fetchYearArticleCounts() {
    const level = schoolLevels[activeSchoolTab];
    if (!level) return;
    
    try {
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'counts:start', data: { school: level.name } }]);
      const counts = {};
      for (const year of level.years) {
        // ✅ Use English keys for API calls (NOT translated labels)
        const url = `${READING_API}/articles?path_type=levels&school=${encodeURIComponent(level.key)}&year=${encodeURIComponent(year.key)}&limit=1000&language=${encodeURIComponent(selectedLanguage || 'English')}`;
        if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'counts:request', data: { url } }]);
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          counts[year.id] = Array.isArray(data) ? data.length : 0;
          if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'counts:response', data: { year: year.name, count: counts[year.id] } }]);
        } else {
          counts[year.id] = 0;
          if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'counts:response:error', data: { status: res.status, statusText: res.statusText } }]);
        }
      }
      setYearArticleCounts(counts);
    } catch (err) {
      logger.error('Error fetching year article counts:', err);
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'counts:error', data: { message: err?.message } }]);
      // Set default counts of 0 if there's an error
      const counts = {};
      level.years.forEach(year => {
        counts[year.id] = 0;
      });
      setYearArticleCounts(counts);
    }
  }

  // Fetch articles for the selected school/year
  async function fetchArticles() {
    setLoading(true);
    setError('');
    const level = schoolLevels[activeSchoolTab];
    const yearObj = level?.years.find(y => y.id === activeYearTab);
    if (!level || !yearObj) return;
    
    // ✅ Use English keys for API calls (NOT translated labels)
    const schoolKey = level.key; // English: "High School"
    const yearKey = yearObj.key; // English: "First Year"
    
    logger.log('Fetching articles for:', {
      school: schoolKey, // English key
      year: yearKey, // English key
      type: 'reading'
    });
    
    try {
      // ✅ API call uses English keys
      const url = `${READING_API}/articles?path_type=levels&school=${encodeURIComponent(schoolKey)}&year=${encodeURIComponent(yearKey)}&language=${encodeURIComponent(selectedLanguage || 'English')}`;
      logger.log('API URL:', url);
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'list:start', data: { url, school: level.name, year: yearObj.name } }]);
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch articles');
      const data = await res.json();
      logger.log('Received articles:', data);
      
      const list = Array.isArray(data) ? data : [];
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'list:success', data: { total: list.length } }]);
      
      // Debug: Log the first article to see its structure
      if (list.length > 0) {
        logger.log('First article structure:', list[0]);
        logger.log('Article content field:', list[0].content);
        logger.log('Article description field:', list[0].description);
      }
      
      if (list.length === 0) {
        // No articles found for this specific combination
        setArticles([]);
        setError(''); // Clear any previous errors
      } else {
        setArticles(list);
        setError('');
      }
      // Update the article count for this year
      setYearArticleCounts(prev => ({
        ...prev,
        [activeYearTab]: list.length
      }));
    } catch (err) {
      logger.error('Error fetching articles:', err);
      // Gracefully degrade to empty state instead of showing error banner
      setError('');
      setArticles([]);
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'list:error', data: { message: err?.message } }]);
    } finally {
      setLoading(false);
    }
  }

  // Add or update article
  async function handleFormSubmit(e) {
    e.preventDefault();
    const level = schoolLevels[activeSchoolTab];
    const yearObj = level?.years.find(y => y.id === activeYearTab);
    if (!formData.title || !formData.contentType || !formData.content) {
      alert(t('messages.fillAllFields'));
      return;
    }
    let articleData = null;
    if (activePath === 'educational-levels') {
      if (!yearObj) { alert(t('messages.selectYear')); return; }
      // ✅ Use English keys for API payload (database expects English)
      articleData = {
        title: formData.title,
        description: cleanHtmlForPreview(formData.content || '', 180),
        content: formData.content,
        language: selectedLanguage || 'English',
        path_type: 'levels',
        estimated_read_time: null,
        word_count: null,
        difficulty: null,
        cover_image: null,
        tags: [],
        status: 'published',
        meta: { school: level.key, year: yearObj.key } // ✅ English keys
      };
    } else if (activePath === 'international-exam') {
      articleData = {
        title: formData.title,
        description: cleanHtmlForPreview(formData.content || '', 180),
        content: formData.content,
        language: selectedLanguage || 'English',
        path_type: 'exam',
        estimated_read_time: null,
        word_count: null,
        difficulty: examDifficulty || null,
        cover_image: null,
        tags: [],
        status: 'published',
        meta: { exam_type: examType, exam_section: examSection, exam_difficulty: examDifficulty || null }
      };
    } else if (activePath === 'esp') {
      articleData = {
        title: formData.title,
        description: cleanHtmlForPreview(formData.content || '', 180),
        content: formData.content,
        language: selectedLanguage || 'English',
        path_type: 'esp',
        estimated_read_time: null,
        word_count: null,
        difficulty: null,
        cover_image: null,
        tags: [],
        status: 'published',
        meta: { faculty: espFaculty, specialty: espSpecialty || 'General', academic_year: espAcademicYear || null }
      };
    } else if (activePath === 'general') {
      articleData = {
        title: formData.title,
        description: cleanHtmlForPreview(formData.content || '', 180),
        content: formData.content,
        language: selectedLanguage || 'English',
        path_type: 'general',
        estimated_read_time: null,
        word_count: null,
        difficulty: null,
        cover_image: null,
        tags: [],
        status: 'published',
        meta: { genre: generalGenre || 'story' }
      };
    }

    try {
      // Debug: Log what we're about to send
      logger.log('Form data before submission:', formData);
      logger.log('Article data to be sent:', articleData);
      
      const url = isEditing 
        ? `${READING_API}/articles/${editArticleId}`
        : `${READING_API}/articles`;
      const method = isEditing ? 'PUT' : 'POST';
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'form:submit', data: { method, url, payload: { ...articleData, content: '[omitted]' } } }]);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(articleData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'form:error', data: { status: response.status, error: errorData?.error } }]);
        throw new Error(errorData.error || 'Failed to save article');
      }

      const savedArticle = await response.json();
      logger.log('Article saved successfully:', savedArticle);
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'form:success', data: { id: savedArticle?.article?.id || editArticleId } }]);
      
      // Refresh articles
      await fetchArticles();
      
      // Close form
      setShowForm(false);
      setIsEditing(false);
      setEditArticleId(null);
      setFormData({
        title: '',
        contentType: '',
        schoolLevel: '',
        year: '',
        content: ''
      });
      
      alert(isEditing ? t('messages.articleUpdated') : t('messages.articleAdded'));
      
    } catch (error) {
      logger.error('Error saving article:', error);
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'form:exception', data: { message: error?.message } }]);
      alert(`Error: ${error.message}`);
    }
  }

  // Delete article
  async function handleDelete(article) {
    if (!window.confirm(t('messages.deleteConfirm'))) {
      return;
    }

    try {
      const url = `${READING_API}/articles/${article.id}`;
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'delete:start', data: { url } }]);
      const response = await fetch(url, {
        method: 'DELETE'
      });

      if (!response.ok) {
        if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'delete:error', data: { status: response.status } }]);
        throw new Error('Failed to delete article');
      }

      // Refresh articles
      await fetchArticles();
      alert(t('messages.articleDeleted'));
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'delete:success', data: { id: article.id } }]);
      
    } catch (error) {
      logger.error('Error deleting article:', error);
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'delete:exception', data: { message: error?.message } }]);
      alert(`Error: ${error.message}`);
    }
  }

  // Delete all articles currently listed in the active section
  async function handleDeleteAllInSection() {
    if (!Array.isArray(articles) || articles.length === 0) return;
    if (!window.confirm(t('messages.deleteAllConfirm', { count: articles.length }))) return;
    try {
      setLoading(true);
      for (const a of articles) {
        try {
          const url = `${READING_API}/articles/${a.id}`;
          if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'bulk-delete:item', data: { id: a.id } }]);
          const res = await fetch(url, { method: 'DELETE' });
          if (!res.ok) throw new Error('Failed');
        } catch (e) {
          // continue
        }
      }
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'bulk-delete:done', data: { total: articles.length } }]);
      // Refresh list after deletions
      setArticles([]);
      if (activePath === 'educational-levels') {
        await fetchArticles();
        await fetchYearArticleCounts();
      }
      alert(t('messages.deleteAllSuccess'));
    } finally {
      setLoading(false);
    }
  }

  // Edit article
  async function handleEdit(article) {
    try {
      setIsEditing(true);
      setEditArticleId(article.id);
      // Load full article (with content and meta)
      const url = `${READING_API}/articles/${article.id}`;
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'edit:load:start', data: { url } }]);
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to load article');
      const full = await res.json();
      setFormData({
        title: full.title || '',
        contentType: 'reading',
        schoolLevel: (full.meta && full.meta.school) || '',
        year: (full.meta && full.meta.year) || '',
        content: full.content || ''
      });
      if (full.path_type === 'exam' && full.meta) {
        setExamType(full.meta.exam_type || 'IELTS');
        setExamSection(full.meta.exam_section || (examSectionsMap[examType] || [])[0] || 'Academic');
        setExamDifficulty(full.meta.exam_difficulty || '');
      } else if (full.path_type === 'esp' && full.meta) {
        setEspFaculty(full.meta.faculty || 'Medicine');
        setEspSpecialty(full.meta.specialty || '');
        setEspAcademicYear(full.meta.academic_year || '');
      } else if (full.path_type === 'general' && full.meta) {
        setGeneralGenre(full.meta.genre || '');
      }
      setShowForm(true);
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'edit:load:success', data: { id: article.id } }]);
    } catch (error) {
      logger.error('Error loading article for edit:', error);
      if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'edit:load:error', data: { message: error?.message } }]);
      alert(t('messages.errorLoading'));
      setIsEditing(false);
      setEditArticleId(null);
    }
  }

  // Open form for new article
  function openForm() {
    logger.log('Opening form for new article...');
    setIsEditing(false);
    setEditArticleId(null);
    setFormData({
      title: '',
      contentType: 'reading', // Set default content type
      schoolLevel: '',
      year: '',
      content: ''
    });
    setShowForm(true);
  }

  // Render the 4-path selection screen
  const renderPathSelection = () => (
    <div className="reading-paths-selection">
      <div className="admin-back-navigation">
        <button className="back-to-admin-btn" onClick={onBack}>
          <span className="material-icons">arrow_back</span>
          {t('backToCourseManagement')}
        </button>
      </div>
      
      <div className="paths-header">
        <h1>📚 {t('title')}</h1>
        <p>{t('selectPath')}</p>
      </div>
      
      <div className="paths-grid">
        {readingPaths.map(path => (
          <div 
            key={path.id} 
            className="path-card"
            onClick={() => setActivePath(path.id)}
            style={{ '--path-color': path.color }}
          >
            <div className="path-icon">{path.icon}</div>
            <h3>{path.title}</h3>
            <p>{path.description}</p>
            <div className="path-arrow">
              <span className="material-icons">arrow_forward</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render the breadcrumb
  const renderBreadcrumb = () => (
    <div className="breadcrumb">
      <span className="breadcrumb-item">{t('breadcrumb.readingManagement')}</span>
      <span className="breadcrumb-sep">→</span>
      <span className="breadcrumb-item">
        {readingPaths.find(p => p.id === activePath)?.title || t('breadcrumb.selectPath')}
      </span>
      {activePath === 'educational-levels' && activeSchoolTab && (
        <>
          <span className="breadcrumb-sep">→</span>
          <span className="breadcrumb-item">{schoolLevels[activeSchoolTab]?.name}</span>
          {activeYearTab && (
            <>
              <span className="breadcrumb-sep">→</span>
              <span className="breadcrumb-item">
                {schoolLevels[activeSchoolTab]?.years.find(y => y.id === activeYearTab)?.name}
              </span>
            </>
          )}
        </>
      )}
    </div>
  );

  // Render the school tabs
  const renderSchoolTabs = () => {
    // For languages that are only studied in high school (e.g. German, Italian, Spanish),
    // only High School is relevant (no primary or middle school)
    const tabsToShow = isHighSchoolOnlyLanguage
      ? schoolTabs.filter(tab => tab.id === 'high-school')
      : schoolTabs;

    return (
      <div className="reading-tabs">
        {tabsToShow.map(tab => (
          <button
            key={tab.id}
            className={`reading-tab ${activeSchoolTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveSchoolTab(tab.id)}
          >
            <span className="material-icons">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>
    );
  };

  // Render the year tabs
  const renderYearTabs = () => {
    const level = schoolLevels[activeSchoolTab];
    if (!level) return null;

    return (
      <div className="year-tabs">
      <div className="year-tabs-header">
        <h3>{level.name} {t('sections.readingContent')}</h3>
        <p>{t('messages.selectYearLevel')}</p>
      </div>
        <div className="year-tabs-container">
          {level.years.map(year => (
            <button
              key={year.id}
              className={`year-tab ${activeYearTab === year.id ? 'active' : ''}`}
              onClick={() => setActiveYearTab(year.id)}
            >
              <span className="material-icons">calendar_today</span>
              {year.name}
              <span className="year-tab-indicator">
                {yearArticleCounts[year.id] || 0} {t('messages.articles')}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Render the content based on active path
  const renderContent = () => {
    if (activePath === 'educational-levels') {
      return renderEducationalLevelsContent();
    } else if (activePath === 'international-exam') {
      return renderInternationalExamContent();
    } else if (activePath === 'esp') {
      return renderESPContent();
    } else if (activePath === 'general') {
      return renderGeneralContent();
    }
    return null;
  };

  // Render Educational Levels content (existing functionality)
  const renderEducationalLevelsContent = () => {
    const level = schoolLevels[activeSchoolTab];
    if (!level) return null;

    if (!activeYearTab) {
      // Show year overview
      return (
        <div className="reading-tab-content">
        <div className="reading-header">
          <h2>{level.name} {t('sections.educationalLevelsManagement')}</h2>
          <p>{t('messages.selectYearLevel')}</p>
        </div>
        
        <div className="year-levels-section">
          <h3>{t('sections.yearLevels')}</h3>
          <div className="year-cards">
            {level.years.map(year => (
              <div key={year.id} className="year-card card-animate">
                <div className="year-card-header">
                  <span className="material-icons">school</span>
                  <h4>{year.name}</h4>
                </div>
                <div className="year-card-content">
                  <p>{yearArticleCounts[year.id] || 0} {t('messages.readingArticles')}</p>
                  <button
                    className="select-year-btn"
                    onClick={() => setActiveYearTab(year.id)}
                  >
                    {t('actions.manageArticles')}
                    <span className="material-icons">arrow_forward</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        </div>
      );
    }

    // Show articles for selected year
    const yearObj = level.years.find(y => y.id === activeYearTab);
    if (!yearObj) return null;

    return (
      <div className="reading-tab-content">
        <div className="reading-header">
          <h2>{level.name} - {yearObj.name} {t('messages.readingArticles')}</h2>
          <p>{t('messages.manageContentFor', { year: yearObj.name })}</p>
        </div>

        <div className="reading-controls">
          <div className="search-box">
            <span className="material-icons">search</span>
            <input type="text" placeholder={t('filters.searchPlaceholder')} />
          </div>
          <button className="add-reading-btn" onClick={openForm}>
            <span className="material-icons">add</span>
            {t('actions.addReadingArticle')}
          </button>
          <button className="add-reading-btn" onClick={handleDeleteAllInSection}>
            <span className="material-icons">delete</span>
            {t('actions.removeAll')}
          </button>
        </div>

        {loading && (
          <div className="loading-state">
            <span className="material-icons">hourglass_empty</span>
            <p>{t('messages.loadingArticles')}</p>
          </div>
        )}

        {!loading && articles.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <span className="material-icons">menu_book</span>
            </div>
            <h3>{t('messages.noArticlesYet')}</h3>
            <p>{t('messages.noArticlesForYear', { year: yearObj.name })}</p>
          </div>
        )}

        {!loading && articles.length > 0 && (
          <div className="content-cards">
            {articles.map(article => (
              <div key={article.id} className="content-card card-animate">
                <div className="content-card-header">
                  <span className={`content-type-badge ${article.type || 'reading'}`}>
                    {t('metadata.readingArticle')}
                  </span>
                  <span className="content-date">
                    {article.date || (article.created_at ? new Date(article.created_at).toLocaleDateString() : '')}
                  </span>
                </div>
                <h4 className="content-title">{article.title}</h4>
                                 <div className="content-preview">
                   <div className="preview-text">
                     {(() => {
                       // Try to get content from different possible fields
                       let contentToShow = article.content || article.description || '';
                       
                       if (contentToShow) {
                         // Clean HTML tags and get plain text
                         const cleanContent = contentToShow.replace(/<[^>]*>/g, '').trim();
                         
                         if (cleanContent.length > 0) {
                           // Show actual content preview
                           const preview = cleanContent.substring(0, 140);
                           return preview.length < cleanContent.length ? `${preview}...` : preview;
                         }
                       }
                       
                       // Fallback only if no content exists
                       return t('messages.noContentPreview');
                     })()}
                   </div>
                 </div>
                <div className="card-bottom-section">
                  <div className="content-actions">
                    <button className="action-btn edit" title={t('actions.edit')} onClick={() => handleEdit(article)}>
                      <span className="material-icons">edit</span>
                      {t('actions.edit')}
                    </button>
                    <button className="action-btn delete" title={t('actions.delete')} onClick={() => handleDelete(article)}>
                      <span className="material-icons">delete</span>
                      {t('actions.delete')}
                    </button>
                  </div>
                  <div className="article-metadata">
                    <div className="metadata-item">
                      <span className="material-icons">school</span>
                      <span>{article.school || level.name}</span>
                    </div>
                    <div className="metadata-item">
                      <span className="material-icons">calendar_today</span>
                      <span>{article.year || yearObj.name}</span>
                    </div>
                    <div className="metadata-item">
                      <span className="material-icons">schedule</span>
                      <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render International Exam content
  const renderInternationalExamContent = () => (
    <div className="reading-tab-content">
      <div className="reading-header">
        <h2>🌍 {t('sections.internationalExamManagement')}</h2>
        <p>{t('paths.internationalExam.description')}</p>
      </div>
      <div className="reading-controls" style={{ gap: 12 }}>
        <select value={examType} onChange={(e) => { setExamType(e.target.value); setExamSection((examSectionsMap[e.target.value] || [])[0] || ''); }}>
          {examTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <select value={examSection} onChange={(e) => setExamSection(e.target.value)}>
          {(examSectionsMap[examType] || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <select value={examDifficulty} onChange={(e) => setExamDifficulty(e.target.value)}>
          <option value="">{t('filters.difficulty')}</option>
          {difficultyOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <button className="add-reading-btn" onClick={() => setShowForm(true)}>
          <span className="material-icons">add</span>
          {t('actions.addReadingArticle')}
        </button>
        <button className="add-reading-btn" onClick={async () => {
          try {
            setLoading(true); setError('');
            const url = `${READING_API}/articles?path_type=exam&exam_type=${encodeURIComponent(examType)}&exam_section=${encodeURIComponent(examSection)}${examDifficulty ? `&exam_difficulty=${encodeURIComponent(examDifficulty)}` : ''}`;
            if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'exam:list:start', data: { url } }]);
            const res = await fetch(url);
            if (!res.ok) throw new Error('Failed to fetch articles');
            const data = await res.json();
            setArticles(Array.isArray(data) ? data : []);
            if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'exam:list:success', data: { total: (Array.isArray(data) ? data.length : 0) } }]);
          } catch (e) {
            setArticles([]); setError('');
            if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'exam:list:error', data: { message: e?.message } }]);
          } finally { setLoading(false); }
        }}>
          <span className="material-icons">refresh</span>
          {t('actions.refresh')}
        </button>
      </div>

      {loading && (
        <div className="loading-state"><span className="material-icons">hourglass_empty</span><p>{t('messages.loadingArticles')}</p></div>
      )}
      {!loading && articles.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon"><span className="material-icons">menu_book</span></div>
          <h3>{t('messages.noArticlesYet')}</h3>
          <p>{t('messages.noArticlesForExam', { examType, examSection })}</p>
        </div>
      )}
      {!loading && articles.length > 0 && (
        <div className="content-cards">
          {articles.map(article => (
            <div key={article.id} className="content-card card-animate">
              <div className="content-card-header">
                <span className={`content-type-badge ${article.type || 'reading'}`}>
                  {t('metadata.examReading', { examType })}
                </span>
                <span className="content-date">
                  {article.date || (article.created_at ? new Date(article.created_at).toLocaleDateString() : '')}
                </span>
              </div>
              <h4 className="content-title">{article.title}</h4>
                             <div className="content-preview">
                 <div className="preview-text">
                   {(() => {
                     // Try to get content from different possible fields
                     let contentToShow = article.content || article.description || '';
                     
                     if (contentToShow) {
                       // Clean HTML tags and get plain text
                       const cleanContent = contentToShow.replace(/<[^>]*>/g, '').trim();
                       
                       if (cleanContent.length > 0) {
                         // Show actual content preview
                         const preview = cleanContent.substring(0, 140);
                         return preview.length < cleanContent.length ? `${preview}...` : preview;
                       }
                     }
                     
                     // Fallback only if no content exists
                     return 'No content preview available. Click Edit to add content.';
                   })()}
                 </div>
               </div>
              <div className="card-bottom-section">
                <div className="content-actions">
                  <button className="action-btn edit" title="Edit this content" onClick={() => handleEdit(article)}>
                    <span className="material-icons">edit</span>
                    Edit
                  </button>
                  <button className="action-btn delete" title="Delete this content" onClick={() => handleDelete(article)}>
                    <span className="material-icons">delete</span>
                    Delete
                  </button>
                </div>
                <div className="article-metadata">
                  <div className="metadata-item">
                    <span className="material-icons">school</span>
                    <span>{examType}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="material-icons">calendar_today</span>
                    <span>{examSection}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="material-icons">schedule</span>
                    <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render ESP content
  const renderESPContent = () => (
    <div className="reading-tab-content">
      <div className="reading-header">
        <h2>🎓 {t('sections.espManagement')}</h2>
        <p>{t('sections.manageSpecialized')}</p>
      </div>
      <div className="reading-controls" style={{ gap: 12, flexWrap: 'wrap' }}>
        <select value={espFaculty} onChange={(e) => setEspFaculty(e.target.value)}>
          {facultyOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <input value={espSpecialty} onChange={(e) => setEspSpecialty(e.target.value)} placeholder={t('filters.specialtyPlaceholder')} />
        <input value={espAcademicYear} onChange={(e) => setEspAcademicYear(e.target.value)} placeholder={t('filters.academicYearPlaceholder')} />
        <button className="add-reading-btn" onClick={() => setShowForm(true)}>
          <span className="material-icons">add</span>
          {t('actions.addReadingArticle')}
        </button>
        <button className="add-reading-btn" onClick={async () => {
          try {
            setLoading(true); setError('');
            const params = new URLSearchParams({ path_type: 'esp', faculty: espFaculty });
            if (espSpecialty) params.set('specialty', espSpecialty);
            if (espAcademicYear) params.set('academic_year', espAcademicYear);
            const url = `${READING_API}/articles?${params.toString()}`;
            if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'esp:list:start', data: { url } }]);
            const res = await fetch(url);
            if (!res.ok) throw new Error('Failed to fetch articles');
            const data = await res.json();
            setArticles(Array.isArray(data) ? data : []);
            if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'esp:list:success', data: { total: (Array.isArray(data) ? data.length : 0) } }]);
          } catch (e) {
            setArticles([]); setError('');
            if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'esp:list:error', data: { message: e?.message } }]);
          } finally { setLoading(false); }
        }}>
          <span className="material-icons">refresh</span>
          {t('actions.refresh')}
        </button>
      </div>

      {loading && (<div className="loading-state"><span className="material-icons">hourglass_empty</span><p>{t('messages.loadingArticles')}</p></div>)}
      {!loading && articles.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon"><span className="material-icons">menu_book</span></div>
          <h3>{t('messages.noArticlesYet')}</h3>
          <p>{t('messages.noArticlesForESP')}</p>
        </div>
      )}
      {!loading && articles.length > 0 && (
        <div className="content-cards">
          {articles.map(article => (
            <div key={article.id} className="content-card card-animate">
              <div className="content-card-header">
                <span className={`content-type-badge ${article.type || 'reading'}`}>
                  {t('metadata.espReading')}
                </span>
                <span className="content-date">
                  {article.date || (article.created_at ? new Date(article.created_at).toLocaleDateString() : '')}
                </span>
              </div>
              <h4 className="content-title">{article.title}</h4>
                             <div className="content-preview">
                 <div className="preview-text">
                   {(() => {
                     // Try to get content from different possible fields
                     let contentToShow = article.content || article.description || '';
                     
                     if (contentToShow) {
                       // Clean HTML tags and get plain text
                       const cleanContent = contentToShow.replace(/<[^>]*>/g, '').trim();
                       
                       if (cleanContent.length > 0) {
                         // Show actual content preview
                         const preview = cleanContent.substring(0, 140);
                         return preview.length < cleanContent.length ? `${preview}...` : preview;
                       }
                     }
                     
                     // Fallback only if no content exists
                     return 'No content preview available. Click Edit to add content.';
                   })()}
                 </div>
               </div>
              <div className="card-bottom-section">
                <div className="content-actions">
                  <button className="action-btn edit" title="Edit this content" onClick={() => handleEdit(article)}>
                    <span className="material-icons">edit</span>
                    Edit
                  </button>
                  <button className="action-btn delete" title="Delete this content" onClick={() => handleDelete(article)}>
                    <span className="material-icons">delete</span>
                    Delete
                  </button>
                </div>
                <div className="article-metadata">
                  <div className="metadata-item">
                    <span className="material-icons">school</span>
                    <span>{espFaculty}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="material-icons">calendar_today</span>
                    <span>{espSpecialty || 'General'}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="material-icons">schedule</span>
                    <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render General content
  const renderGeneralContent = () => (
    <div className="reading-tab-content">
      <div className="reading-header">
        <h2>📖 {t('sections.generalManagement')}</h2>
        <p>{t('sections.manageGeneral')}</p>
      </div>
      <div className="reading-controls" style={{ gap: 12, flexWrap: 'wrap' }}>
        <select value={generalGenre} onChange={(e) => setGeneralGenre(e.target.value)}>
          <option value="">{t('filters.genreAll')}</option>
          {generalGenres.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <button className="add-reading-btn" onClick={() => setShowForm(true)}>
          <span className="material-icons">add</span>
          {t('actions.addReadingArticle')}
        </button>
        <button className="add-reading-btn" onClick={async () => {
          try {
            setLoading(true); setError('');
            const params = new URLSearchParams({ path_type: 'general' });
            if (generalGenre) params.set('genre', generalGenre);
            const url = `${READING_API}/articles?${params.toString()}`;
            if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'general:list:start', data: { url } }]);
            const res = await fetch(url);
            if (!res.ok) throw new Error('Failed to fetch articles');
            const data = await res.json();
            setArticles(Array.isArray(data) ? data : []);
            if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'general:list:success', data: { total: (Array.isArray(data) ? data.length : 0) } }]);
          } catch (e) {
            setArticles([]); setError('');
            if (debugEnabled) setDebugEvents(prev => [...prev, { ts: new Date().toISOString(), event: 'general:list:error', data: { message: e?.message } }]);
          } finally { setLoading(false); }
        }}>
          <span className="material-icons">refresh</span>
          {t('actions.refresh')}
        </button>
      </div>
      {loading && (<div className="loading-state"><span className="material-icons">hourglass_empty</span><p>{t('messages.loadingArticles')}</p></div>)}
      {!loading && articles.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon"><span className="material-icons">menu_book</span></div>
          <h3>{t('messages.noArticlesYet')}</h3>
          <p>{t('messages.noArticlesForGeneral')}</p>
        </div>
      )}
      {!loading && articles.length > 0 && (
        <div className="content-cards">
          {articles.map(article => (
            <div key={article.id} className="content-card card-animate">
              <div className="content-card-header">
                <span className={`content-type-badge ${article.type || 'reading'}`}>
                  {t('metadata.generalReading')}
                </span>
                <span className="content-date">
                  {article.date || (article.created_at ? new Date(article.created_at).toLocaleDateString() : '')}
                </span>
              </div>
              <h4 className="content-title">{article.title}</h4>
                             <div className="content-preview">
                 <div className="preview-text">
                   {(() => {
                     // Try to get content from different possible fields
                     let contentToShow = article.content || article.description || '';
                     
                     if (contentToShow) {
                       // Clean HTML tags and get plain text
                       const cleanContent = contentToShow.replace(/<[^>]*>/g, '').trim();
                       
                       if (cleanContent.length > 0) {
                         // Show actual content preview
                         const preview = cleanContent.substring(0, 140);
                         return preview.length < cleanContent.length ? `${preview}...` : preview;
                       }
                     }
                     
                     // Fallback only if no content exists
                     return 'No content preview available. Click Edit to add content.';
                   })()}
                 </div>
               </div>
              <div className="card-bottom-section">
                <div className="content-actions">
                  <button className="action-btn edit" title="Edit this content" onClick={() => handleEdit(article)}>
                    <span className="material-icons">edit</span>
                    Edit
                  </button>
                                      <button className="action-btn delete" title="Delete this content" onClick={() => handleDelete(article)}>
                      <span className="material-icons">delete</span>
                      Delete
                    </button>
                </div>
                <div className="article-metadata">
                  <div className="metadata-item">
                    <span className="material-icons">school</span>
                    <span>{generalGenre || 'General'}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="material-icons">calendar_today</span>
                    <span>English</span>
                  </div>
                  <div className="metadata-item">
                    <span className="material-icons">schedule</span>
                    <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render the form modal
  const renderForm = () => {
    if (!showForm) return null;

    return (
      <div className={`reading-overlay ${showForm ? 'show' : ''}`}>
        <div className="reading-form">
          <div className="form-header">
            <h3>{isEditing ? t('form.editArticle') : t('form.addNewArticle')}</h3>
            <button className="close-btn" onClick={() => setShowForm(false)}>
              <span className="material-icons">close</span>
            </button>
          </div>
          
          <form onSubmit={handleFormSubmit} className="form-content">
            <div className="form-group">
              <label htmlFor="title">{t('form.articleTitle')}</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder={t('form.titlePlaceholder')}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="contentType">{t('form.contentType')}</label>
              <select
                id="contentType"
                value={formData.contentType}
                onChange={(e) => setFormData({...formData, contentType: e.target.value})}
                required
              >
                <option value="">{t('form.selectContentType')}</option>
                <option value="reading">{t('form.readingArticle')}</option>
                <option value="vocabulary">{t('form.vocabulary')}</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="content">{t('form.content')}</label>
              {useQuill ? (
                <div ref={quillRef} style={{ height: '200px' }}></div>
              ) : (
                <textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder={t('form.contentPlaceholder')}
                  style={{ 
                    width: '100%', 
                    minHeight: '200px', 
                    padding: '12px', 
                    border: '2px solid #e1e5e9', 
                    borderRadius: '8px', 
                    fontSize: '14px',
                    fontFamily: 'Arial, sans-serif',
                    resize: 'vertical'
                  }}
                  required
                />
              )}
            </div>
            
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                {t('actions.cancel')}
              </button>
              <button type="submit" className="btn-primary">
                {isEditing ? t('actions.updateArticle') : t('actions.addArticle')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Main render logic
  if (!activePath) {
    return renderPathSelection();
  }

  return (
    <div className="reading-management-scroll-wrapper">
      <div className="reading-management-page">
        {/* Debug panel */}
        {debugEnabled && (
          <div style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff7e6', border: '1px solid #ffd591', padding: 8, marginBottom: 12, borderRadius: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>Debug Log</strong>
              <button className="btn-secondary" onClick={() => setDebugEvents([])}>Clear</button>
            </div>
            <div style={{ maxHeight: 160, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
              {(debugEvents || []).slice(-50).map((e, i) => (
                <div key={i}>
                  [{e.ts}] {e.event} {e.data ? JSON.stringify(e.data) : ''}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="path-navigation">
          <button className="back-to-paths-btn" onClick={() => setActivePath(null)}>
            <span className="material-icons">arrow_back</span>
            {t('backToReadingPaths')}
          </button>
        </div>
        
        {renderBreadcrumb()}
        
        {activePath === 'educational-levels' && (
          <>
            {renderSchoolTabs()}
            {renderYearTabs()}
          </>
        )}
        
        {renderContent()}
        {renderForm()}
      </div>
    </div>
  );
};

export default Reading; 